import { Navigate, useParams } from "react-router-dom";
import PageHero from "../../components/sections/PageHero";
import GoldButton from "../../components/ui/GoldButton";
import SectionLabel from "../../components/ui/SectionLabel";
import StatCounter from "../../components/ui/StatCounter";
import { useCaseStudyBySlug } from "../../hooks/useCMSData";

const Spinner = () => (
  <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{ width: 32, height: 32, borderRadius: "50%", borderTop: "2px solid #D4A843", animation: "spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const WorkCaseStudy = () => {
  const { slug = "" } = useParams();
  const { data: caseStudy, loading } = useCaseStudyBySlug(slug);

  if (loading) return <Spinner />;

  if (!caseStudy) {
    return <Navigate to="/work" replace />;
  }

  return (
    <>
      <PageHero
        label={caseStudy.label}
        title={caseStudy.title}
        titleAccent={caseStudy.titleAccent}
        subtitle={caseStudy.subtitle}
      />

      <section style={{ background: "#050505", padding: "0 0 110px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 clamp(20px, 4vw, 48px)" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.1fr 0.9fr",
              gap: 32,
              marginBottom: 56,
            }}
          >
            <div
              style={{
                background: "#0E0E0E",
                border: "1px solid rgba(212,168,67,0.18)",
                borderRadius: 4,
                padding: "32px clamp(24px, 4vw, 36px)",
              }}
            >
              <SectionLabel text="Overview" />
              <p
                style={{
                  marginTop: 20,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 15,
                  color: "#F8F4EC",
                  lineHeight: 1.8,
                }}
              >
                {caseStudy.summary}
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 18,
              }}
            >
              {caseStudy.stats.map((stat) => (
                <div
                  key={stat.label}
                  style={{
                    background: "#0E0E0E",
                    border: "1px solid rgba(212,168,67,0.18)",
                    borderRadius: 4,
                    padding: "28px 12px",
                  }}
                >
                  <StatCounter value={stat.value} label={stat.label} />
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 24,
              marginBottom: 42,
            }}
          >
            {[
              { title: "Challenge", items: caseStudy.challenge },
              { title: "Solution", items: caseStudy.solution },
              { title: "Impact", items: caseStudy.impact },
            ].map((section) => (
              <div
                key={section.title}
                style={{
                  background: "#0E0E0E",
                  border: "1px solid rgba(212,168,67,0.18)",
                  borderRadius: 4,
                  padding: "28px 24px",
                }}
              >
                <SectionLabel text={section.title} />
                <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 18 }}>
                  {section.items.map((item) => (
                    <div
                      key={item}
                      style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "flex-start",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 14,
                        color: "#F8F4EC",
                        lineHeight: 1.7,
                      }}
                    >
                      <span style={{ color: "#D4A843", flexShrink: 0 }}>•</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              background: "#0E0E0E",
              border: "1px solid rgba(212,168,67,0.18)",
              borderRadius: 4,
              padding: "28px 24px",
            }}
          >
            <SectionLabel text="Technology Stack" />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 18 }}>
              {caseStudy.techStack.map((item) => (
                <span
                  key={item}
                  style={{
                    padding: "9px 16px",
                    border: "1px solid rgba(212,168,67,0.25)",
                    borderRadius: 3,
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    color: "#D4A843",
                    background: "rgba(212,168,67,0.06)",
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 52 }}>
            <GoldButton href="/contact">DISCUSS A SIMILAR PROJECT</GoldButton>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkCaseStudy;
